import React, { useState } from 'react';
import type {
  PiV2Call,
  PiV2CallStatus,
  PiV2CallTranscriptLine,
} from '../../../patient-insurance/typesV2';
import { Icon } from '../../Icon/Icon';
import { IconButton } from '../../IconButton/IconButton';
import { ViewportDrawer } from '../../Overlay/ViewportDrawer';
import { Pill } from '../../Pill/Pill';
import { PatientProfileSectionCard } from '../PatientProfileSectionCard';

type Tone = 'success' | 'gold' | 'warning' | 'neutral';

const STATUS_TONE: Record<PiV2CallStatus, Tone> = {
  completed: 'success',
  partial: 'gold',
  failed: 'warning',
  no_answer: 'neutral',
  disconnected: 'neutral',
  needs_review: 'gold',
};

const SPEAKER_LABEL: Record<PiV2CallTranscriptLine['speaker'], string> = {
  elva: 'Elva',
  representative: 'Payer rep',
  ivr: 'IVR',
};

const COLS = 'grid grid-cols-[1.4fr_0.9fr_1.6fr_0.9fr_0.6fr] items-center gap-3';

export interface PatientInsuranceCallHistoryProps {
  calls: PiV2Call[];
}

/**
 * Insurance Call History (spec §15) — an audit trail / evidence layer, not a
 * knowledge base staff must manage. Rows are selectable and open a right-side
 * Call Detail drawer (§16). No staff-triggered "Call insurance" action in V1.
 */
export function PatientInsuranceCallHistory({ calls }: PatientInsuranceCallHistoryProps) {
  // `open` drives the drawer; `selected` holds the call to render. They are kept
  // apart so the exit animation can play out while the last call is still shown.
  const [selected, setSelected] = useState<PiV2Call | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <PatientProfileSectionCard icon="phone" title="Insurance call history" accent="var(--ai-3)">
        {calls.length === 0 ? (
          <div>
            <p className="m-0 text-[13px] font-semibold text-[var(--ink-1)]">No insurance calls yet</p>
            <p className="m-0 mt-0.5 text-[12px] text-[var(--ink-3)]">
              Electronic and document sources currently provide the available insurance information.
            </p>
          </div>
        ) : (
          <>
            <div
              className={`${COLS} pb-2 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink-4)]`}
            >
              <span>Date / time</span>
              <span>Payer</span>
              <span>Reason</span>
              <span>Result</span>
              <span className="text-right">Duration</span>
            </div>
            {calls.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setSelected(c);
                  setOpen(true);
                }}
                className={`${COLS} w-full border-t border-[var(--cream-2)] py-2.5 text-left text-[12.5px] hover:bg-[var(--cream-0)]`}
              >
                <span className="text-[var(--ink-1)]">{c.at}</span>
                <span className="font-semibold text-[var(--ink-0)]">{c.payer}</span>
                <span className="min-w-0 truncate text-[var(--ink-2)]">{c.reason}</span>
                <span>
                  <Pill tone={STATUS_TONE[c.status]}>{c.statusLabel}</Pill>
                </span>
                <span className="text-right font-[var(--font-mono)] text-[var(--ink-3)]">
                  {c.durationLabel}
                </span>
              </button>
            ))}
          </>
        )}
      </PatientProfileSectionCard>

      <ViewportDrawer
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        onExited={() => {
          setSelected(null);
        }}
        width="min(520px, 92vw)"
      >
        {selected ? (
          <CallDrawerContent
            call={selected}
            onClose={() => {
              setOpen(false);
            }}
          />
        ) : null}
      </ViewportDrawer>
    </>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <>
      <span className="text-[var(--ink-3)]">{label}</span>
      <span className="font-medium text-[var(--ink-1)]">{value}</span>
    </>
  );
}

const KICKER = 'font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.12em] text-[var(--ink-4)]';

/**
 * Right-side Call Detail drawer body (spec §16). Rendered inside a
 * {@link ViewportDrawer}, which portals to document.body so the panel is anchored
 * to the viewport (full window height) rather than the transformed page container.
 * No "Ask ELVA / View extracted answers".
 */
function CallDrawerContent({ call, onClose }: { call: PiV2Call; onClose: () => void }) {
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const hasTranscript = call.transcript != null && call.transcript.length > 0;

  return (
    <div className="flex h-full flex-col">
      <div style={{ height: 3, background: 'var(--grad-spectrum)', flex: 'none' }} />
      <div className="flex items-start gap-3 border-b border-[var(--cream-2)] px-5 py-4">
        <div className="min-w-0 flex-1">
          <div className="font-[var(--font-display)] text-[15px] font-semibold text-[var(--ink-0)]">
            Insurance call details
          </div>
          <div className="mt-1 flex items-center gap-2 text-[12px] text-[var(--ink-3)]">
            {call.payer}
            <Pill tone={STATUS_TONE[call.status]}>{call.statusLabel}</Pill>
          </div>
        </div>
        <IconButton icon="x" size={32} title="Close" onClick={onClose} />
      </div>

      <div className="cal-scroll flex-1 overflow-y-auto px-5 py-4">
        <div className={KICKER}>Call details</div>
        <div className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12.5px]">
          <Meta label="Insurance company" value={call.payer} />
          {call.calledNumber != null ? <Meta label="Phone called" value={call.calledNumber} /> : null}
          <Meta label="Date & time" value={call.at} />
          <Meta label="Duration" value={call.durationLabel} />
          <Meta label="Reason" value={call.reason} />
          {call.representative != null ? (
            <Meta label="Representative" value={call.representative} />
          ) : null}
          {call.referenceNumber != null ? (
            <Meta label="Reference #" value={call.referenceNumber} />
          ) : null}
          <Meta label="Result" value={call.statusLabel} />
        </div>

        {call.summary != null && call.summary !== '' ? (
          <div className="mt-4">
            <div className={KICKER}>Call summary</div>
            <p className="m-0 mt-1.5 text-[13px] leading-[1.55] text-[var(--ink-1)]">
              {call.summary}
            </p>
          </div>
        ) : null}

        <div className="mt-4">
          <div className={KICKER}>Recording</div>
          {call.recordingUrl != null && call.recordingUrl !== '' ? (
            <audio controls src={call.recordingUrl} className="mt-1.5 w-full">
              <track kind="captions" />
            </audio>
          ) : (
            <p className="m-0 mt-1.5 text-[12px] text-[var(--ink-4)]">
              Recording not available for this call.
            </p>
          )}
        </div>

        {hasTranscript ? (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => {
                setTranscriptOpen((v) => !v);
              }}
              className="flex w-full items-center justify-between rounded-[var(--r-sm)] py-1 text-left"
              aria-expanded={transcriptOpen}
            >
              <span className={KICKER}>Transcript</span>
              <Icon kind={transcriptOpen ? 'up' : 'down'} size={14} color="var(--ink-4)" />
            </button>
            {transcriptOpen ? (
              <div className="mt-2 flex flex-col gap-2">
                {(call.transcript ?? []).map((line, i) => (
                  <div key={i} className="text-[12.5px] leading-[1.5]">
                    <span
                      className="font-[var(--font-mono)] text-[10.5px] font-bold uppercase tracking-[0.06em]"
                      style={{
                        color: line.speaker === 'elva' ? 'var(--ai-3)' : 'var(--ink-3)',
                      }}
                    >
                      {SPEAKER_LABEL[line.speaker]}
                    </span>{' '}
                    <span className="text-[var(--ink-1)]">{line.text}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
